import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  isOverChildTicketPointLimit,
  totalResolvedPoint,
  type BasicDesignPeople,
  type ChildTicketRow,
} from '@cortex/domain-product-innovation-lab-basic-design-tree';
import {
  useExpandBasicDesign,
  useExpandBasicDesignChunks,
} from '../../../hooks/useBasicDesignExpansion.js';
import { FassychanLoadingOverlay } from '../../ui/FassychanLoadingOverlay/index.js';
import { ChildTicketExpandDialog } from './ChildTicketExpandDialog.js';
import {
  ChildTicketExpandResultDialog,
  type ExpandResult,
} from './ChildTicketExpandResultDialog.js';
import type { ChildTicketChunk } from './child-ticket-chunks.js';

/** 展開のかけ方。`sheet` は 1 枚のシート、`chunks` はチャンクに分けて。 */
export type ExpandMode = 'sheet' | 'chunks';

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計展開の一連の流れ（確認ダイアログ → 待機画面 → 結果ダイアログ）。シート 1 枚の展開とチャンク分け展開のどちらも同じ確認を通し、押した後の行き先だけを変える
 * @graph-connects ChildTicketExpandDialog [calls] 展開前の確認
 * @graph-connects ChildTicketExpandResultDialog [calls] 展開後の結果
 * @graph-connects useExpandBasicDesign [calls] シート 1 枚の展開
 * @graph-connects useExpandBasicDesignChunks [calls] チャンク分け展開
 */
export function ChildTicketExpandFlow({
  pjKey,
  mode,
  rows,
  chunks,
  people,
  isDevLeader,
  onClose,
  onCancelled,
  onExpanded,
}: {
  /** 母票（チャンク分けでは爺票になる票）の課題キー。 */
  pjKey: string;
  /** 開いている確認の種類。null なら閉じている。 */
  mode: ExpandMode | null;
  /** シート 1 枚を展開するときの行。 */
  rows: readonly ChildTicketRow[];
  /** チャンク分けで展開するチャンク（画面の並び順）。 */
  chunks: readonly ChildTicketChunk[];
  /** 母票から取れる人。 */
  people: BasicDesignPeople;
  /** 今見ている人が開発リーダーか。 */
  isDevLeader: boolean;
  /** 確認を閉じた（何もしない）。 */
  onClose: () => void;
  /** 展開を取り消した（開発リーダーの引き受けを断った）。 */
  onCancelled: () => void;
  /** 展開を投げた（成否は問わない。呼び出し側は編集状態を閉じる）。 */
  onExpanded: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const [result, setResult] = useState<ExpandResult | null>(null);
  const expand = useExpandBasicDesign(pjKey);
  const expandChunks = useExpandBasicDesignChunks(pjKey);

  const failed = (error: Error): void => {
    setResult({ kind: 'failed', reason: error.message });
  };
  const schedule = { sheetUrl: window.location.href };

  return (
    <>
      <ChildTicketExpandDialog
        open={mode !== null}
        totalPoint={
          mode === 'chunks'
            ? chunks.reduce((sum, chunk) => sum + totalResolvedPoint(chunk.rows), 0)
            : totalResolvedPoint(rows)
        }
        // チャンク分けでは「どれか 1 つでも 80pt を超えていないか」を見る。合計で見ると
        // 分けた意味が無くなり、分けた直後でも必ず警告が出る
        overPointLimit={
          mode === 'chunks'
            ? chunks.some((chunk) => isOverChildTicketPointLimit(totalResolvedPoint(chunk.rows)))
            : isOverChildTicketPointLimit(totalResolvedPoint(rows))
        }
        isDevLeader={isDevLeader}
        devLeaderName={people.devLeader}
        onClose={onClose}
        onCancelled={onCancelled}
        onConfirmed={(confirmed) => {
          onExpanded();
          if (mode === 'chunks') {
            expandChunks.mutate(
              {
                // 並びがそのまま「統合プロダクト内リリース順」になる
                chunks: chunks.map((chunk, index) => ({
                  chunkId: chunk.chunkId,
                  order: index + 1,
                  title: chunk.title,
                  rows: [...chunk.rows],
                })),
                ...confirmed,
                ...schedule,
              },
              {
                onSuccess: (done) => {
                  setResult({
                    kind: 'done',
                    // 母票も並べる。チャンク分けでまず見たいのは母票のキー
                    issueKeys: done.chunks.flatMap((chunk) => [
                      chunk.motohyoKey,
                      ...chunk.children.map((child) => child.issueKey),
                    ]),
                    removed: done.chunks.flatMap((chunk) => chunk.removed),
                    retained: done.chunks.flatMap((chunk) => chunk.retained),
                    totalPoint: done.chunks.reduce((sum, chunk) => sum + chunk.totalPoint, 0),
                  });
                },
                onError: failed,
              },
            );
            return;
          }
          expand.mutate(
            // 基本設計の子票 description に残して、Backlog からシートへ戻れるようにする
            { rows: [...rows], ...confirmed, ...schedule },
            {
              onSuccess: (done) => {
                setResult({
                  kind: 'done',
                  issueKeys: done.children.map((child) => child.issueKey),
                  removed: done.removed,
                  retained: done.retained,
                  totalPoint: done.totalPoint,
                });
              },
              onError: failed,
            },
          );
        }}
      />
      <ChildTicketExpandResultDialog
        result={result}
        onClose={() => {
          setResult(null);
        }}
      />
      {/* 展開は子票の枚数ぶん Backlog を叩くので数十秒かかる。起票と同じ待機画面で
          「進んでいる」ことを見せ、二度押しも塞ぐ */}
      {expand.isPending || expandChunks.isPending ? (
        <FassychanLoadingOverlay
          portal
          zIndexClassName="z-[calc(var(--z-modal)+1)]"
          label={t('childTicketPlan.expand.running')}
        />
      ) : null}
    </>
  );
}
